import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FLASHCARD_SESSION_SIZE_OPTIONS, type FlashcardSessionSize, Language } from "@/lib/constant";
import type { FlashcardSettings } from "@/types/common";

type OptionsModalProps = {
  open: boolean;
  settings: FlashcardSettings;
  onUpdate: (patch: Partial<FlashcardSettings>) => void;
  onRestart: () => void;
  onClose: () => void;
};

export function OptionsModal({ open, settings, onUpdate, onRestart, onClose }: OptionsModalProps) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-4" style={{ boxShadow: "none" }}>
        {/* 옵션 타이틀 */}
        <DialogTitle className="text-center text-md font-bold text-foreground">옵션</DialogTitle>

        <div className="flex flex-col gap-3">
          {/* 세션 카드 수 */}
          <section className="rounded-lg bg-gray-50 p-4">
            <div className="mb-2.5">
              <p className="text-sm font-semibold text-foreground">한 번에 학습할 카드</p>
              <p className="mt-1 text-xs text-muted-foreground">다음 세션부터 적용돼요.</p>
            </div>
            <Tabs
              value={String(settings.sessionSize)}
              onValueChange={value =>
                onUpdate({ sessionSize: (value === "all" ? "all" : Number(value)) as FlashcardSessionSize })
              }
              className="w-full"
            >
              <TabsList className="w-full gap-1 py-4">
                {FLASHCARD_SESSION_SIZE_OPTIONS.map(size => (
                  <TabsTrigger key={size} value={String(size)} className="w-full py-3 text-foreground! cursor-pointer">
                    {size === "all" ? "전체" : size}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </section>

          {/* 일반 옵션 */}
          <section className="rounded-lg bg-gray-50 p-4 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-foreground">카드 셔플</p>
              <Switch
                checked={settings.shuffleCards}
                onCheckedChange={checked => onUpdate({ shuffleCards: checked })}
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-foreground">자동 읽기</p>
                <Switch
                  disabled={settings.frontSide !== Language.ENGLISH}
                  checked={settings.ttsEnabled}
                  onCheckedChange={checked => onUpdate({ ttsEnabled: checked })}
                />
              </div>

              <p className="mt-1 text-xs text-muted-foreground leading-relaxed">카드를 넘길 때마다 단어를 읽어줘요.</p>
            </div>
          </section>

          {/* 카테고리별 분류하기 */}
          <section className="rounded-lg bg-gray-50 p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-foreground">카테고리별 분류하기</p>
              <Switch
                checked={settings.trackCategories}
                onCheckedChange={checked => onUpdate({ trackCategories: checked })}
              />
            </div>

            <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
              <b>알고 있음</b>과 <b>학습 중</b>으로 낱말카드를 분류해요.
              <br />
              낱말카드를 빠르게 복습하려면 옵션을 해제해요.
            </p>
          </section>

          {/* 카드 방향 */}
          <section className="rounded-lg bg-gray-50 p-4">
            <div className="flex flex-col items-start justify-between gap-2.5 w-full">
              <p className="text-sm font-semibold text-foreground">
                카드 방향<span className="text-xs text-muted-foreground"> (앞면)</span>
              </p>

              <Tabs
                value={settings.frontSide}
                onValueChange={value => onUpdate({ frontSide: value as "english" | "korean" })}
                className="w-full"
              >
                <TabsList className="w-full py-4 gap-1">
                  <TabsTrigger value="english" className="w-full py-3 text-foreground! cursor-pointer">
                    영어
                  </TabsTrigger>
                  <TabsTrigger value="korean" className="w-full py-3 text-foreground! cursor-pointer">
                    한국어
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </section>

          {/* 낱말카드 다시 시작하기 */}
          <Button
            type="button"
            size="lg"
            className="w-full py-6 text-destructive hover:text-destructive"
            variant="ghost"
            onClick={onRestart}
          >
            낱말카드 다시 시작하기
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
